import React, { useState, useEffect } from 'react';
import { ShoppingBag, Menu } from 'lucide-react';
import Logo from '../common/Logo';
import { getCategories } from '../../data/storeData';

const Navbar = ({ isScrolled, cart, onCartOpen, onMobileMenuOpen, setActiveCategory }) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();

    const handleUpdate = () => loadCategories();
    window.addEventListener('categoriesUpdated', handleUpdate);

    return () => {
      window.removeEventListener('categoriesUpdated', handleUpdate);
    };
  }, []);

  const scrollToCatalog = (categoryId) => {
    setActiveCategory(categoryId);
    const catalog = document.getElementById('catalog');
    if (catalog) {
      catalog.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);

  return (
    <nav
      className={`fixed w-full z-40 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isScrolled
          ? 'bg-black/70 backdrop-blur-2xl py-3 shadow-[0_1px_0_rgba(255,255,255,0.08),0_10px_40px_-10px_rgba(0,0,0,0.6)]'
          : 'bg-gradient-to-b from-black/60 to-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between text-[#f5f5f7]">
        {/* Left: Logo + Links */}
        <div className="flex items-center gap-10 lg:gap-14">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="cursor-pointer transition-all duration-300 hover:opacity-80 active:scale-95"
            aria-label="Back to top"
          >
            <Logo className={`transition-all duration-500 ${isScrolled ? 'h-8 md:h-9' : 'h-10 md:h-12'} text-[#f5f5f7]`} />
          </button>

          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => scrollToCatalog('all')}
              className="px-4 py-2 rounded-full text-[14px] font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
            >
              Store
            </button>

            {categories.slice(0, 4).map((category) => (
              <button
                key={category.id}
                onClick={() => scrollToCatalog(category.id)}
                className="px-4 py-2 rounded-full text-[14px] font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Cart + Mobile Menu */}
        <div className="flex items-center gap-3">
          <button
            onClick={onCartOpen}
            className="relative p-2.5 rounded-full hover:bg-white/10 transition-all duration-300 active:scale-90 group"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-[22px] h-[22px] text-gray-200 group-hover:text-white transition-colors" strokeWidth={1.5} />
            {cartCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-[0_0_12px_rgba(249,115,22,0.6)] animate-[cartPop_0.35s_cubic-bezier(0.34,1.56,0.64,1)]">
                {cartCount}
              </span>
            )}
          </button>

          <button
            className="md:hidden p-2.5 rounded-full hover:bg-white/10 transition-all duration-300 active:scale-90"
            onClick={onMobileMenuOpen}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-white" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes cartPop {
          0% { transform: scale(0); }
          70% { transform: scale(1.25); }
          100% { transform: scale(1); }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
